import usePrevious from 'hooks/usePrevious';
import { isArray, isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ADD_DELETE_REQUEST,
    GET_DOWNLOAD_DOCUMENT,
    REMOVE_DELETE_REQUEST,
    UPDATE_APPLICATION_DETAIL_POST_DATA,
    UPDATE_DECLARATION_NORMAL_POST_DATA,
    DELETE_DECLARATION_NORMAL_DATA,
} from 'store/key';
import { generateRandomId, produce, toHalfWidthUnicode } from 'utility/general';
import API from 'api/call';
import { setGlobalLoading } from 'store/global/reducer';

const useCoInvestigatorData = ({ formId, siteInfoId }) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const dispatch = useDispatch();
    const serverTableKey = 'coi';
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const [rows, setRows] = useState(remoteData ?? []);
    const previousData = usePrevious(remoteData);

    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    const removeAllTempData = () => {
        setRows(
            rows.filter((item) => {
                return !item.id.toString().startsWith('temp_');
            }),
        );
    };

    // useEffect(() => {
    //     setRows([...remoteData, ...(updatePostData ?? [])]);
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [updatePostData]);

    const [choice] = useState({
        majorResponse: [
            { key: 0, label: 'Lead PI' },
            { key: 1, label: 'Administration' },
            { key: 2, label: 'Clinical Care' },
            { key: 3, label: 'Data/Sample Security' },
            { key: 4, label: 'Documentation' },
            { key: 5, label: 'Efficacy & Safety' },
            { key: 6, label: 'Expertise Advice' },
            { key: 7, label: 'Informed Consent' },
            { key: 8, label: 'Partners Coordinator' },
            { key: 9, label: 'Protection of Subject' },
            { key: 10, label: 'Site Coordinator' },
            { key: 11, label: 'Staff Safety' },
            { key: 12, label: 'Subject Recruitment' },
        ],
    });

    const defaultFormList = {
        title: {
            value: '',
            label: 'Title',
            name: 'title',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
        surname: {
            value: '',
            label: 'Surname',
            name: 'surname',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
        firstName: {
            value: '',
            label: 'First Name',
            name: 'firstName',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
        email: {
            value: '',
            label: 'Email',
            name: 'email',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
            showErrorWhenOnBlur: true,
        },
        department: {
            value: '',
            label: 'Department',
            name: 'department',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
        position: {
            value: '',
            label: 'Position',
            name: 'position',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
        majorResponse: {
            value: '',
            label: 'Major Responsibility',
            name: 'majorResponse',
            type: 'select',
            operators: choice['majorResponse'],
            validators: ['isRequired'],
        },
        otherRespond: {
            value: '',
            label: 'Other Responsibility (if applicable)',
            name: 'otherRespond',
            type: 'input',
            maxLength: 255,
        },
        hospitalCode: {
            value: '',
            label: 'Hospital / Institution',
            name: 'hospitalCode',
            type: 'hospitalOrInstitution',
            showOtherOption: true,
            validators: ['isRequired'],
        },
        others: {
            value: '',
            label: 'Others, please specify',
            name: 'others',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
            isDisabled: (formList) => {
                const disabled = formList.hospitalCode.value !== 'OTHERS';
                if (disabled) {
                    formList.others.value = '';
                    formList.others.validators = [];
                } else {
                    formList.others.validators = ['isRequired'];
                }
                return disabled;
            },
        },
        cvFileName: {
            value: [],
            label: 'CV File',
            name: 'cvFileName',
            type: 'uploadFile',
            multiple: false,
            validators: ['isRequired'],
            enableRemove: true,
            showFileSize: 1,
        },
    };

    const getChoiceLabel = (type, key) => {
        return (
            choice[type]?.find((item) => {
                return item.key === key;
            })?.label ?? 'undefined'
        );
    };

    const [columns] = useState([
        { field: 'title', headerName: 'Title', width: 80 },
        { field: 'surname', headerName: 'Surname', width: 100 },
        { field: 'firstName', headerName: 'First Name', width: 100 },
        { field: 'email', headerName: 'Email', width: 150 },
        { field: 'department', headerName: 'Department', width: 100 },
        { field: 'position', headerName: 'Position', width: 100 },
        {
            field: 'majorResponse',
            headerName: 'Major Responsibility',
            width: 150,
            valueGetter: (params) => {
                const value = params.getValue('majorResponse');
                return getChoiceLabel('majorResponse', value);
            },
        },
        { field: 'otherRespond', headerName: 'Other Responsibility', width: 150 },
        { field: 'hospitalCode', headerName: 'Hospital / Institution', width: 100 },
        { field: 'others', headerName: 'Others, please specify', width: 100 },
        {
            field: 'cvFileName',
            headerName: 'CV',
            width: 120,
            valueGetter: (params) => {
                const fileName = params.getValue('cvFileName') ?? 'error';
                let isNewlyCreate = params.row.id.toString().startsWith('temp_');
                if (params.row.editCvFile) {
                    isNewlyCreate = true;
                }

                return (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <div
                        style={
                            isNewlyCreate
                                ? {}
                                : {
                                      color: 'blue',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                  }
                        }
                        onClick={() => {
                            if (isNewlyCreate) {
                                return;
                            }

                            dispatch({
                                type: GET_DOWNLOAD_DOCUMENT,
                                payload: {
                                    data: {
                                        id: params.row.id,
                                        type: 'coi',
                                    },
                                },
                            });
                        }}
                    >
                        {fileName}
                    </div>
                );
            },
        },
    ]);

    const onEditSubmit = async (row) => {
        row.siteInfo = { id: siteInfoId };
        let postData = updatePostData ?? [];

        const index = postData.findIndex((item) => {
            return item.id === row.id;
        });

        if (isArray(row.cvFileName) && row.cvFileName.length) {
            let formData = new FormData();
            row.cvFileName.forEach((item) => {

                let file = item;

                try {
                    // convert the file name to all half width unicode for fix of code scan issue 
                    file = new File([item], toHalfWidthUnicode(item.name), {
                        type: item.type,
                    });
                } catch (error) {
                    console.log('%c 121359 PopupInput.jsx error', 'background: green', error)
                }

                formData.append('file', file);
            });
            const res = await API.file.uploadDocument(formData);
            row.uploadTempId = res.data.result.result[0].key;
            row.cvFileName = res.data.result.result[0].fileName;
            row.editCvFile = true;
            if (index === -1) {
                postData.push(row);
            } else {
                postData[index] = row;
            }
        } else {
            if (index === -1) {
                postData.push(row);
            } else {
                postData[index] = row;
            }
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                draft[index] = row;
            }),
        );
    };

    const onRecordUpdate = async (data) => {
        try {
            setGlobalLoading(true);
            let formData = new FormData();

            data.cvFileName.forEach((item) => {

                let file = item;

                try {
                    // convert the file name to all half width unicode for fix of code scan issue 
                    file = new File([item], toHalfWidthUnicode(item.name), {
                        type: item.type,
                    });
                } catch (error) {
                    console.log('%c 121359 PopupInput.jsx error', 'background: green', error)
                }

                formData.append('file', file);
            });

            const res = await API.file.uploadDocument(formData);

            data.id = `temp_${generateRandomId()}`;
            data.uploadTempId = res.data.result.result[0].key;
            data.siteInfo = { id: siteInfoId };

            data.cvFileName = res.data.result.result[0].fileName;

            const newRows = produce(rows, (draft) => {
                draft.push(data);
            });
            setRows(newRows);

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: [...(updatePostData ?? []), data],
                    applicationFormId: formId,
                },
            });

            /** update declaration normal table  */
            dispatch({
                type: UPDATE_DECLARATION_NORMAL_POST_DATA,
                payload: {
                    formId,
                    siteInfoId,
                    row: data,
                    roleNameIndex: 1,
                },
            });
        } catch (error) {
            console.log('105553 useCoInvestigatorData.js error', error);
        }

        setGlobalLoading(false);
    };

    const handleDelete = async (row, rowIndex) => {
        /** delete declaration normal table row */
        dispatch({
            type: DELETE_DECLARATION_NORMAL_DATA,
            payload: {
                formId,
                siteInfoId,
                deletionCoiRowIndex: rowIndex,
            },
        });

        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });

            return;
        }

        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: {
                type: serverTableKey,
                id: row.id,
                siteInfoId,
                applicationFormId: formId,
            },
        });
    };

    const recoverDataCreation = async () => {
        // handle edited data recovery
        const updatedPostData = [];

        rows.forEach((item) => {
            const remoteDataObject = remoteData.find((remoteItem) => {
                return remoteItem.id === item.id;
            });

            if (remoteDataObject) {
                const isSame = Object.entries(item).every(([key, value]) => {
                    // remove exception
                    if (key === 'siteInfo' || key === 'applicationForm') {
                        return true;
                    }

                    return remoteDataObject[key] === value;
                });

                if (!isSame) {
                    item.siteInfo = { id: siteInfoId };
                    updatedPostData.push(item);
                }
            }
        });

        const postData = rows.filter((row) => {
            const isDataCreation = row.id.toString().startsWith('temp_');

            if (!isDataCreation) {
                dispatch({
                    type: REMOVE_DELETE_REQUEST,
                    payload: {
                        type: serverTableKey,
                        id: row.id,
                        applicationFormId: formId,
                    },
                });
            }

            return isDataCreation;
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...postData, ...updatedPostData],
                applicationFormId: formId,
            },
        });
    };

    const emptyTableData = async () => {
        remoteData
            .filter((item) => item.siteInfoId === siteInfoId)
            .forEach((row) => {
                dispatch({
                    type: ADD_DELETE_REQUEST,
                    payload: {
                        type: serverTableKey,
                        id: row.id,
                        siteInfoId,
                        applicationFormId: formId,
                    },
                });
            });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [],
                applicationFormId: formId,
            },
        });
    };

    const handleEditClick = (row) => {
        setSelectedRow(row);
        setOpenPopup(true);
    };

    const handleRowCreate = async (id) => {
        setSelectedRow(null);
        setOpenPopup(true);
    };

    const getFilteredRows = () => {
        return rows.filter((item) => item.siteInfo.id === siteInfoId);
    };

    return {
        columns,
        rows: getFilteredRows(),
        setRows,
        openPopup,
        setOpenPopup,
        defaultFormList,
        selectedRow,
        handleRowCreate,
        onRecordUpdate,
        handleEditClick,
        handleDelete,
        onEditSubmit,
        recoverDataCreation,
        emptyTableData,
        removeAllTempData,
    };
};

export default useCoInvestigatorData;
